// ============================================================================
// src/lib/feedOrdersApi.ts — the ppp-feed-orders-v1 write, merged onto a FRESH read
// ----------------------------------------------------------------------------
// ONE app_store blob carries TWO surfaces' orders. `ppp-feed-orders-v1` is a
// single JSON document holding four maps:
//
//     {pig: {...}, starter: {...}, grower: {...}, layerfeed: {...}}
//
// `pig` is /pig/feed's whole subject. `starter` / `grower` / `layerfeed` are
// /broiler/feed's. Neither page renders the other's map, but both pages SAVE the
// whole object — the blob store has no partial update — so every save by one
// half rewrites the other half's orders from whatever base the saver happened to
// be holding.
//
// WHY THAT WAS SAFE AND WHY IT STOPPED BEING SAFE. It was safe because the base
// was App's shared `feedOrders` state: ONE object both views were handed, which
// every save updated through `setFeedOrders`. BroilerFeedView's own header says
// so — "App's `feedOrders` state is also PigFeedView's, so a poultry order that
// did not call setFeedOrders would leave /pig/feed showing a stale blob for the
// rest of the session." The shared mutable object WAS the coupling, and the
// fan-out WAS the fix.
//
// Both views have since localized their READ: each takes its half of the blob
// from its own route resource (broiler.feed.core / pig.feed.core) plus a local
// optimistic override, and neither reads App's state any more. The moment either
// surface is released from legacy boot there is no App state to keep in step at
// all — and then the base each view merges into is a per-mount snapshot taken
// when that page loaded. Save on /pig/feed, navigate to /broiler/feed on a
// cached resource, save there: the broiler save writes the pig map as it was
// before the pig save, and the operator's pig order is gone with no error.
//
// This is the repo's `whole-blob write over a possibly-stale base` class, and
// the fix is the one the class takes: do not write a base you were handed, write
// a base you just read. `saveFeedOrdersPatch` reads ppp-feed-orders-v1 at SAVE
// TIME, merges the caller's patch into that, and writes the result. A broiler
// save then preserves whatever the pig map is on the server at that instant, and
// a pig save preserves the three poultry maps, whether or not App's state exists.
//
// WHY THE DECISION IS A SEPARATE, PURE, EXPORTED FUNCTION. The same reason
// `resolveProcessorClearPlan` is one beside `planProcessorClear` in
// src/livestock/WeighInSessionPage.tsx: the ORDER is the whole correctness
// argument — the read must be awaited BEFORE anything is decided — and an order
// stated as a signature is provable without a renderer. `resolveFeedOrdersSavePlan`
// is the merge, with no client and no I/O; `planFeedOrdersSave` takes the read as
// a PARAMETER, so a test can prove the read happened, that it happened first, and
// that the payload arriving after the click is what the write is built from.
//
// IT FAILS CLOSED, AND THAT IS THE POINT OF THE PLAN TYPE. A blob store cannot
// express "update one key of the document". If the fresh read is unavailable
// there is no correct base, and the two available fallbacks are both destructive:
// merging onto `{}` publishes a document missing every map the caller did not
// patch, and merging onto the caller's stale copy is the defect this module
// exists to remove. So an unavailable read produces `{kind: 'unresolved'}` and
// NOTHING is written. A refused save is recoverable — the operator retries. A
// half-blob is not: it silently deletes the other surface's orders.
//
// AN ABSENT ROW IS NOT AN UNAVAILABLE ONE, AND MUST NOT BECOME `{}`. Both view
// headers spell out what an absent ppp-feed-orders-v1 row leaves App's state at:
// "the same four maps App seeds. Not `{}`, and the difference is not cosmetic."
// The route data catalog reproduces that seed for the same reason. So a
// SUCCESSFUL read that finds no row resolves to NO_FEED_ORDERS and the save
// proceeds — that is a farm that has never ordered feed, not a failure. Only a
// failed read, and a row holding something that is not a JSON object, refuse.
//
// WHAT DELIBERATELY DID NOT MOVE. The DECISIONS stay in the views, because they
// are UI decisions: which month is active, what a blank draft means when the
// recommendation is zero, whether a persisted row passes through unchanged, and
// the optimistic local publish that repaints the page before the write resolves.
// This module owns the base and the merge — nothing else. What each view hands
// over is a PATCH describing only its own maps, which is also what makes the
// merge auditable: a patch that never mentions `pig` cannot modify `pig`.
// ============================================================================

import type {SupabaseClient, SupabaseErrorLike} from './supabaseTypes.js';
import {
  getActiveFeedRequestFence,
  patchFeedOrdersOnServer,
  type AppStoreConfigFenceOptions,
} from './appStoreConfigOperationApi.js';
import type {SaveStatusSink} from './appStoreBlobApi.js';

/**
 * The blob as both views read it: map name -> 'YYYY-MM' -> lbs ordered.
 *
 * The index signature is load-bearing rather than lazy typing. A round-trip
 * through this module must not drop a map it never heard of, so the merge
 * spreads the whole fresh document and replaces only the maps named in the
 * patch. Naming exactly four keys here would invite a future reader to narrow
 * it, and narrowing it is how the fifth map would get deleted.
 */
export type FeedOrdersBlob = Record<string, Record<string, unknown>>;

/**
 * App's own `useState` seed for ppp-feed-orders-v1, and what the boot fan-out
 * leaves that state at when the row is absent — see `src/App.tsx` and the
 * matching constants in both views and in the route data catalog. This is the
 * base an ABSENT row resolves to. It is emphatically not `{}`.
 */
export const NO_FEED_ORDERS: FeedOrdersBlob = Object.freeze({
  pig: {},
  starter: {},
  grower: {},
  layerfeed: {},
});

/**
 * What one view asks to change, expressed as map -> 'YYYY-MM' -> value.
 *
 * `null` means DELETE that month, which is /pig/feed's "clear the order" path
 * (`savePigOrder` with an empty string). A number is a save, including 0 —
 * both boards treat a persisted 0 as "decision made", so 0 and absent are
 * different states and the patch has to be able to express each.
 */
export type FeedOrdersPatch = Record<string, Record<string, number | null>>;

/**
 * The result of the fresh read, with "the row is absent" and "the read failed"
 * kept apart. They resolve to opposite outcomes — seed versus refuse — so a
 * shape that flattened them into one nullable value would make the fail-closed
 * branch unreachable, which is the exact bug class this module is fixing.
 */
export type FreshFeedOrdersRead = {ok: true; row: unknown} | {ok: false; error: SupabaseErrorLike | null};

/** Why a save refused. Surfaced to the operator; never silently swallowed. */
export type FeedOrdersRefusal = 'read-failed' | 'read-malformed';

export type FeedOrdersSavePlan = {kind: 'save'; next: FeedOrdersBlob} | {kind: 'unresolved'; reason: FeedOrdersRefusal};

/**
 * Why the server write itself did not confirm. `refused`: a bound 260 receipt
 * recorded `outcome: 'refused'` (authoritative server refusal). `rejected`: the
 * operation raised a raw SQLSTATE (in `code`). `unconfirmed`: no bound reply
 * confirmed it, so it may still have landed — only a retry with the SAME
 * request id can tell.
 */
export type FeedOrdersWriteFailure = 'rejected' | 'unconfirmed';

export type FeedOrdersSaveResult =
  | {ok: true; next: FeedOrdersBlob}
  | {ok: false; reason: FeedOrdersRefusal}
  | {ok: false; reason: 'refused'; code: 'refused'; detailReason: string; message: string}
  | {ok: false; reason: FeedOrdersWriteFailure; code?: string; message: string}
  | {ok: false; reason: 'not_sent'; message: string};

/**
 * A JSON object, as opposed to the other things a `jsonb` column can hold.
 *
 * Arrays are rejected on purpose: `{...['a']}` is `{0: 'a'}`, so spreading an
 * array base would produce a document that is neither the array nor a valid
 * blob, and would do it silently.
 */
function isJsonObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * THE MERGE, and the whole correctness argument, as a pure function.
 *
 * `fresh` is the just-read state of the row. `patch` names only the maps the
 * caller owns. Every key of the fresh document that the patch does not name is
 * carried over BY REFERENCE, unexamined and unmodified — which is what makes
 * "a broiler save preserves the pig map exactly" a structural property rather
 * than something the caller has to remember to do.
 *
 * Refuses — `unresolved`, nothing to write — when the read failed, or when the
 * row holds something that is not a JSON object. An ABSENT row is neither: it
 * is a farm with no orders yet, and it resolves to the four-map seed.
 */
export function resolveFeedOrdersSavePlan(
  fresh: FreshFeedOrdersRead | null | undefined,
  patch: FeedOrdersPatch,
): FeedOrdersSavePlan {
  // An absent payload is not an empty one, and neither is a failed read. A
  // caller that could not produce a result at all is in the same position as one
  // whose query errored: there is no base, so there is no correct write.
  if (!fresh || fresh.ok !== true) return {kind: 'unresolved', reason: 'read-failed'};
  // `null`/`undefined` is the ABSENT row — the seed, and a legitimate base.
  // Anything else that is not a JSON object is a document this module cannot
  // merge into without destroying it, so it refuses rather than guessing.
  let base: FeedOrdersBlob;
  if (fresh.row == null) base = NO_FEED_ORDERS;
  else if (isJsonObject(fresh.row)) base = fresh.row as FeedOrdersBlob;
  else return {kind: 'unresolved', reason: 'read-malformed'};

  const next: FeedOrdersBlob = {...base};
  for (const mapKey of Object.keys(patch)) {
    const entries = patch[mapKey];
    if (!entries) continue;
    // Copied, never mutated in place: `base` may be the frozen seed, and it may
    // also be the very object a caller is still rendering from.
    const map: Record<string, unknown> = {...(isJsonObject(base[mapKey]) ? base[mapKey] : {})};
    for (const ym of Object.keys(entries)) {
      const value = entries[ym];
      if (value === null) delete map[ym];
      else map[ym] = value;
    }
    next[mapKey] = map;
  }
  return {kind: 'save', next};
}

/**
 * Read ppp-feed-orders-v1 as it is on the server RIGHT NOW.
 *
 * Two unwraps, both load-bearing: `.maybeSingle()` yields the ROW or null, and
 * the row's own `data` column holds the blob. A missing row therefore surfaces
 * as `{ok: true, row: undefined}` — success, no document — which is what keeps
 * "never ordered feed" from being reported as a failure.
 *
 * The key is spelled as a LITERAL in the PostgREST chain, deliberately.
 * `app_store_boundary_static` censuses the literal keys reachable in each owner
 * module; hoisting it to a constant would make that guard blind to this module
 * while every test naming the key still passed. Do not "clean this up".
 */
export async function readFreshFeedOrders(sb: SupabaseClient): Promise<FreshFeedOrdersRead> {
  try {
    const r = await sb.from('app_store').select('data').eq('key', 'ppp-feed-orders-v1').maybeSingle();
    if (r.error) return {ok: false, error: r.error};
    return {ok: true, row: r.data ? r.data.data : null};
  } catch (error) {
    // A rejected read is a failed read. Left uncaught it would reject
    // `saveFeedOrdersPatch`, whose callers never had a rejection to handle
    // because App's `sbSave` never threw — and an unhandled rejection here would
    // be indistinguishable from a save that quietly did nothing.
    return {ok: false, error: (error as SupabaseErrorLike) || null};
  }
}

/**
 * The save decision, with the ORDER it depends on made explicit and injectable.
 *
 * The read is awaited BEFORE anything is decided and its result is the only base
 * — which is the fix, stated as a signature rather than as a comment. Modelled
 * on `planProcessorClear` in src/livestock/WeighInSessionPage.tsx for exactly
 * that reason: `readFresh` being a parameter is what lets a test prove the write
 * is built from a payload that arrived AFTER the operator clicked Save.
 */
export async function planFeedOrdersSave(
  readFresh: () => Promise<FreshFeedOrdersRead | null>,
  patch: FeedOrdersPatch,
): Promise<FeedOrdersSavePlan> {
  return resolveFeedOrdersSavePlan(await readFresh(), patch);
}

/**
 * Apply `patch` to ppp-feed-orders-v1, merged onto a read taken at save time.
 *
 * Resolves either way and never rejects, matching `saveAppStoreBlob` and the
 * `sbSave` prop it replaces at both call sites. A refusal drives the same
 * `error` save-status the transport's own failure path drives, so the header
 * indicator is truthful even when nothing was attempted; the returned reason
 * lets the caller say WHICH failure it was in the page.
 *
 * `ok: true` means the server confirmed the write (saved or no change). A
 * rejected or unconfirmed operation is `ok: false` too. `requestId` is
 * optional: a caller that retries one intent passes the same id each time, so
 * the server's receipt replays a write that already landed; without it the
 * API mints one per call, as before.
 */
export async function saveFeedOrdersPatch(
  sb: SupabaseClient,
  patch: FeedOrdersPatch,
  onSaveStatus: SaveStatusSink,
  requestId?: string,
  fenceArg?: AppStoreConfigFenceOptions,
): Promise<FeedOrdersSaveResult> {
  const fence = fenceArg ?? getActiveFeedRequestFence(requestId);
  if (fence?.beforeAttempt && !(await fence.beforeAttempt())) {
    return {
      ok: false,
      reason: 'not_sent',
      message: 'The signed-in account changed before sending. Your entry is kept.',
    };
  }
  const plan = await planFeedOrdersSave(() => readFreshFeedOrders(sb), patch);
  if (plan.kind !== 'save') {
    // FAIL CLOSED. No upsert is attempted at all: there is no base, and every
    // blob this could write instead would delete orders it never read.
    console.error('ppp-feed-orders-v1 save refused — could not read the current blob:', plan.reason);
    onSaveStatus('error');
    setTimeout(() => onSaveStatus(''), 4000);
    return {ok: false, reason: plan.reason};
  }
  const outcome = await patchFeedOrdersOnServer(sb, patch, onSaveStatus, requestId, fence);
  if (outcome.status === 'not_sent') {
    return {ok: false, reason: 'not_sent', message: outcome.message};
  }
  if (outcome.status === 'refused') {
    return {
      ok: false,
      reason: 'refused',
      code: 'refused',
      detailReason: outcome.reason,
      message: 'The server refused this order. Your entry is kept.',
    };
  }
  if (outcome.status === 'rejected') {
    return {ok: false, reason: 'rejected', code: outcome.code, message: outcome.message};
  }
  if (outcome.status === 'unknown') {
    return {ok: false, reason: 'unconfirmed', message: outcome.message};
  }
  if (
    (outcome.status === 'saved' || outcome.status === 'no_change') &&
    outcome.data !== null &&
    typeof outcome.data === 'object' &&
    !Array.isArray(outcome.data)
  ) {
    return {ok: true, next: outcome.data as FeedOrdersBlob};
  }
  return {ok: true, next: plan.next};
}
